import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ── Shared SVGs ── */
const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

const Divider = () => (
  <div className="home-std__divider page-rule">
    <div className="rule-line rule-line--short"></div>
    <div className="rule-diamond"></div>
    <div className="rule-line rule-line--short"></div>
  </div>
);

/* ── Days-since helpers ── */
const WEDDING_DATE = new Date('2026-09-06T11:00:00');

function pad2(n: number) { return String(n).padStart(2, '0'); }

function getTimeSince() {
  const diff = Math.max(0, Date.now() - WEDDING_DATE.getTime());
  const totalSecs = Math.floor(diff / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins  = Math.floor((totalSecs % 3600) / 60);
  const secs  = totalSecs % 60;
  return {
    d: days >= 100 ? String(days) : String(days).padStart(2, '0'),
    h: pad2(hours),
    m: pad2(mins),
    s: pad2(secs),
  };
}

/* ── Component ── */
const Home: React.FC = () => {
  const [cd, setCd] = useState(getTimeSince);
  const heroBgRef = useRef<HTMLDivElement>(null);
  const namesRef = useRef<HTMLHeadingElement>(null);


  /* days-since counter */
  useEffect(() => {
    const id = setInterval(() => setCd(getTimeSince()), 1000);
    return () => clearInterval(id);
  }, []);

  /* parallax */
  useEffect(() => {
    let raf = false;
    const onScroll = () => {
      if (!raf) {
        requestAnimationFrame(() => {
          if (heroBgRef.current) {
            heroBgRef.current.style.transform = `translateY(${window.scrollY * 0.22}px)`;
          }
          raf = false;
        });
        raf = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* letter animation */
  useEffect(() => {
    const el = namesRef.current;
    if (!el) return;
    const name = 'Ashley & A.J.';
    el.innerHTML = '';
    name.split('').forEach(ch => {
      const span = document.createElement('span');
      span.className = 'char' + (ch === ' ' ? ' space' : '');
      span.textContent = ch === ' ' ? '\u00a0' : ch;
      el.appendChild(span);
    });
    const chars = el.querySelectorAll<HTMLSpanElement>('.char');
    chars.forEach((c, i) => {
      setTimeout(() => c.classList.add('visible'), i * 65 + 500);
    });
  }, []);

  return (
    <main>
      {/* ── HERO ── */}
      <section className="home-hero">
        {/* photo panel */}
        <div className="home-hero__photo">
          {/* NOTE: place your hero photo in public/ as hero-photo.jpg
              or update this filename to match yours */}
          <div
            ref={heroBgRef}
            className="home-hero__photo-bg"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/AB4A9451_Original.jpeg)` }}
          />
          <div className="home-hero__photo-overlay" />
          <div className="home-hero__scroll-cue">
            <div className="home-hero__scroll-line" />
          </div>
        </div>

        {/* text panel */}
        <div className="home-hero__text">

          {/* anchor lockup */}
          <div className="home-hero__anchor-lockup">
            <span className="home-hero__anchor-aa">A &nbsp; A</span>
            <AnchorSVG className="home-hero__anchor-svg" />
          </div>

          {/* top rule */}
          <div className="home-hero__rule page-rule">
            <div className="rule-line rule-line--short"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--short"></div>
          </div>

          {/* names — letter animation fills this */}
          <h1 className="home-hero__names" ref={namesRef}></h1>

          {/* bottom rule */}
          <div className="home-hero__bottom-rule page-rule">
            <div className="rule-line rule-line--short"></div>
            <div className="rule-diamond"></div>
            <div className="rule-line rule-line--short"></div>
          </div>

          <p className="home-hero__welcome">Welcome Aboard</p>
          <p className="home-hero__date">Sunday, September 6th, 2026</p>
          <p className="home-hero__location">Jefferson Beach Marina · St. Clair Shores, MI</p>

          <Link to="/details" className="home-hero__cta">
            Wedding Details
          </Link>
        </div>
      </section>

      {/* ── COUNTDOWN ── */}
      <section className="home-countdown">
        <div className="home-countdown__wave">
          <svg viewBox="0 0 1440 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" width="100%" height="50">
            <path d="M0,25 C360,50 720,0 1080,25 C1260,37 1380,15 1440,25 L1440,0 L0,0 Z" fill="#faf3f1"/>
          </svg>
        </div>
        <AnchorSVG className="home-countdown__anchor" />
        <p className="section-eyebrow home-countdown__label">We&rsquo;re Married!</p>
        <div className="home-countdown__grid">
          <div className="home-countdown__unit">
            <span className="home-countdown__number">{cd.d}</span>
            <span className="home-countdown__unit-label">Days</span>
          </div>
          <span className="home-countdown__sep">·</span>
          <div className="home-countdown__unit">
            <span className="home-countdown__number">{cd.h}</span>
            <span className="home-countdown__unit-label">Hours</span>
          </div>
          <span className="home-countdown__sep">·</span>
          <div className="home-countdown__unit">
            <span className="home-countdown__number">{cd.m}</span>
            <span className="home-countdown__unit-label">Minutes</span>
          </div>
          <span className="home-countdown__sep">·</span>
          <div className="home-countdown__unit">
            <span className="home-countdown__number">{cd.s}</span>
            <span className="home-countdown__unit-label">Seconds</span>
          </div>
        </div>
      </section>

      {/* ── SAVE THE DATE ── */}
      <section className="home-std">
        <div className="home-std__card">
          <AnchorSVG className="home-std__anchor" />
          <p className="home-std__aa">A &nbsp; A</p>
          <p className="home-std__request">
            Together with our families<br/>
            please join us<br/>
            for the wedding of
          </p>
          <h2 className="home-std__names">Ashley &amp; A.J.</h2>
          <Divider />
          <p className="home-std__venue">Jefferson Beach Marina</p>
          <p className="home-std__city">St. Clair Shores, Michigan</p>
          <p className="home-std__date">Ceremony at 11:00am · Sunday, September 6th, 2026</p>
          <Link to="/details" className="home-std__cta">View Wedding Details</Link>
        </div>
      </section>

      {/* ── NAV CARDS ── */}
      <section className="home-cards">
        <p className="section-eyebrow section-eyebrow--muted home-cards__label">Explore</p>
        <div className="home-cards__grid">

          <Link to="/story" className="home-card">
            <div className="home-card__img" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/IMG_2760.jpg)` }} />
            <div className="home-card__vignette" />
            <div className="home-card__body">
              <p className="home-card__eyebrow">How it started</p>
              <p className="home-card__title">Our Story</p>
              <p className="home-card__link">Read more <span>→</span></p>
            </div>
          </Link>

          <Link to="/details" className="home-card">
            {/* NOTE: add sean-oulashin-KMn4VEeEPR8-unsplash.jpg to public/ */}
            <div className="home-card__img" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/AB4A9542_Original.jpg.jpeg)` }} />
            <div className="home-card__vignette" />
            <div className="home-card__body">
              <p className="home-card__eyebrow">All the details</p>
              <p className="home-card__title">Wedding Details</p>
              <p className="home-card__link">View details <span>→</span></p>
            </div>
          </Link>

          <Link to="/qa" className="home-card">
            <div className="home-card__img" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/368B47AF-B50B-4384-B1F3-CC01E05165B0_1_105_c.jpeg)` }} />
            <div className="home-card__vignette" />
            <div className="home-card__body">
              <p className="home-card__eyebrow">Questions</p>
              <p className="home-card__title">Q&amp;A</p>
              <p className="home-card__link">Explore <span>→</span></p>
            </div>
          </Link>

        </div>
      </section>
    </main>
  );
};

export default Home;
